export type DirectSkuOperation = "retire" | "restore" | "exclude";

export interface DirectSkuCommand {
  op: DirectSkuOperation;
  codes: string[];
}

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Resolve explicit SKU-code commands in the browser against the complete
 * catalog. The chat model only receives a sample for large stores, so exact
 * bulk removals must not depend on whether a code happened to be in that sample.
 */
export function inferDirectSkuCommand(message: string, skuCodes: string[]): DirectSkuCommand | null {
  const text = message.trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  let op: DirectSkuOperation | null = null;
  if (/\b(restore|undelete|bring back|put back|unremove)\b/.test(lower)) op = "restore";
  else if (/\b(set aside|not (?:a |an )?product|coupon|shipping line|test item|tracking code)\b/.test(lower)) op = "exclude";
  else if (/\b(delete|remove|retire|discontinued?|no longer sell|don'?t sell|do not sell|old skus?)\b/.test(lower)) op = "retire";
  if (!op) return null;

  const namesCodes = /\b(skus?|codes?)\b/i.test(text);
  const matches = skuCodes.flatMap((code) => {
    const trimmed = code.trim();
    if (!trimmed) return [];
    if (/^\d{1,2}$/.test(trimmed) && !namesCodes) return [];
    const hit = new RegExp(`(^|[^a-z0-9])(${escapeRegex(trimmed)})($|[^a-z0-9])`, "i").exec(text);
    if (!hit || hit.index === undefined) return [];
    const start = hit.index + hit[1]!.length;
    return [{ code, start, end: start + trimmed.length }];
  });
  // Prefer the complete code when one code sits inside another (OLD-1 must not
  // also match the separate one-character code "1").
  const codes = matches
    .filter((m) => !matches.some((other) => other !== m && other.start <= m.start && other.end >= m.end && (other.end - other.start) > (m.end - m.start)))
    .map((m) => m.code);
  return codes.length > 0 ? { op, codes: [...new Set(codes)] } : null;
}
